import { useTranslation } from 'react-i18next';
import {
  useRef,
  useEffect,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import type {
  PhotoAnnotation,
  AnnotationType,
  PenData,
  ArrowData,
  TextData,
} from '@/api/photos.api';

export interface AnnotationCanvasHandle {
  clearUnsaved: () => void;
}

interface AnnotationCanvasProps {
  width: number;
  height: number;
  tool: AnnotationType;
  color: string;
  drawMode: boolean;
  savedAnnotations: PhotoAnnotation[];
  onAnnotationComplete: (
    type: AnnotationType,
    data: PenData | ArrowData | TextData,
  ) => void;
  onDeleteAnnotation: (id: string) => void;
  onMoveAnnotation?: (id: string, data: PenData | ArrowData | TextData) => void;
}

interface Point {
  x: number;
  y: number;
}

function drawArrow(
  ctx: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  color: string,
  lineWidth = 2.5,
) {
  const headLen = 14;
  const angle = Math.atan2(y2 - y1, x2 - x1);
  ctx.save();
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(
    x2 - headLen * Math.cos(angle - Math.PI / 6),
    y2 - headLen * Math.sin(angle - Math.PI / 6),
  );
  ctx.lineTo(
    x2 - headLen * Math.cos(angle + Math.PI / 6),
    y2 - headLen * Math.sin(angle + Math.PI / 6),
  );
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

function drawPen(
  ctx: CanvasRenderingContext2D,
  points: Point[],
  color: string,
  lineWidth = 2.5,
) {
  if (points.length < 2) return;
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length; i++) {
    ctx.lineTo(points[i].x, points[i].y);
  }
  ctx.stroke();
  ctx.restore();
}

function drawText(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  text: string,
  color: string,
  fontSize = 16,
) {
  ctx.save();
  ctx.font = `bold ${fontSize}px sans-serif`;
  ctx.fillStyle = color;
  ctx.strokeStyle = 'rgba(0,0,0,0.6)';
  ctx.lineWidth = 3;
  ctx.strokeText(text, x, y);
  ctx.fillText(text, x, y);
  ctx.restore();
}

function renderAnnotation(ctx: CanvasRenderingContext2D, ann: PhotoAnnotation) {
  if (ann.type === 'pen') {
    const d = ann.data as PenData;
    drawPen(ctx, d.points, ann.color);
  } else if (ann.type === 'arrow') {
    const d = ann.data as ArrowData;
    drawArrow(ctx, d.x1, d.y1, d.x2, d.y2, ann.color);
  } else if (ann.type === 'text') {
    const d = ann.data as TextData;
    drawText(ctx, d.x, d.y, d.text, ann.color, d.fontSize);
  }
}

export const AnnotationCanvas = forwardRef<
  AnnotationCanvasHandle,
  AnnotationCanvasProps
>(function AnnotationCanvas(
  {
    width,
    height,
    tool,
    color,
    drawMode,
    savedAnnotations,
    onAnnotationComplete,
    onDeleteAnnotation,
    onMoveAnnotation,
  },
  ref,
) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);
  const currentPoints = useRef<Point[]>([]);
  const startPoint = useRef<Point | null>(null);

  // Move/drag state
  const movingAnnotation = useRef<{
    id: string;
    type: AnnotationType;
    origData: PenData | ArrowData | TextData;
    grabX: number;
    grabY: number;
  } | null>(null);
  const [movePreview, setMovePreview] = useState<PhotoAnnotation | null>(null);

  // Text input state
  const [textInput, setTextInput] = useState<{
    x: number;
    y: number;
    value: string;
  } | null>(null);
  const textInputRef = useRef<HTMLInputElement>(null);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    for (const ann of savedAnnotations) {
      // If this annotation is being moved, render the preview position instead
      if (movePreview && movePreview.id === ann.id) {
        renderAnnotation(ctx, movePreview);
      } else {
        renderAnnotation(ctx, ann);
      }
    }
  }, [savedAnnotations, movePreview]);

  useEffect(() => {
    redraw();
  }, [redraw]);

  useImperativeHandle(ref, () => ({
    clearUnsaved: () => {
      currentPoints.current = [];
      startPoint.current = null;
      isDrawing.current = false;
      redraw();
    },
  }));

  const getPos = (e: React.MouseEvent<HTMLCanvasElement>): Point => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (width / rect.width),
      y: (e.clientY - rect.top) * (height / rect.height),
    };
  };

  const translateAnnotation = (
    ann: PhotoAnnotation,
    dx: number,
    dy: number,
  ): PenData | ArrowData | TextData => {
    if (ann.type === 'pen') {
      const d = ann.data as PenData;
      return { points: d.points.map((p) => ({ x: p.x + dx, y: p.y + dy })) };
    }
    if (ann.type === 'arrow') {
      const d = ann.data as ArrowData;
      return { x1: d.x1 + dx, y1: d.y1 + dy, x2: d.x2 + dx, y2: d.y2 + dy };
    }
    const d = ann.data as TextData;
    return { ...d, x: d.x + dx, y: d.y + dy };
  };

  const findNearestAnnotation = (pos: Point, threshold = 30): string | null => {
    let nearest: { id: string; dist: number } | null = null;
    for (const ann of savedAnnotations) {
      let dist = Infinity;
      if (ann.type === 'pen') {
        const d = ann.data as PenData;
        for (const p of d.points) {
          const dx = p.x - pos.x;
          const dy = p.y - pos.y;
          dist = Math.min(dist, Math.sqrt(dx * dx + dy * dy));
        }
      } else if (ann.type === 'arrow') {
        const d = ann.data as ArrowData;
        const mx = (d.x1 + d.x2) / 2;
        const my = (d.y1 + d.y2) / 2;
        dist = Math.sqrt((mx - pos.x) ** 2 + (my - pos.y) ** 2);
      } else if (ann.type === 'text') {
        const d = ann.data as TextData;
        dist = Math.sqrt((d.x - pos.x) ** 2 + (d.y - pos.y) ** 2);
      }
      if (!nearest || dist < nearest.dist) nearest = { id: ann.id, dist };
    }
    return nearest && nearest.dist < threshold ? nearest.id : null;
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!drawMode) return;
    if (tool === 'text') return; // text handled on click
    const pos = getPos(e);

    if (tool === 'move') {
      const hitId = findNearestAnnotation(pos, 40);
      if (!hitId) return;
      const ann = savedAnnotations.find((a) => a.id === hitId);
      if (!ann) return;
      movingAnnotation.current = {
        id: ann.id,
        type: ann.type,
        origData: ann.data,
        grabX: pos.x,
        grabY: pos.y,
      };
      isDrawing.current = true;
      return;
    }

    isDrawing.current = true;
    startPoint.current = pos;
    if (tool === 'pen') {
      currentPoints.current = [pos];
    } else if (tool === 'eraser') {
      const hit = findNearestAnnotation(pos, 40);
      if (hit) onDeleteAnnotation(hit);
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!drawMode || !isDrawing.current) return;
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext('2d')!;
    const pos = getPos(e);

    if (tool === 'move' && movingAnnotation.current) {
      const m = movingAnnotation.current;
      const dx = pos.x - m.grabX;
      const dy = pos.y - m.grabY;
      const ann = savedAnnotations.find((a) => a.id === m.id);
      if (!ann) return;
      setMovePreview({
        ...ann,
        data: translateAnnotation({ ...ann, data: m.origData }, dx, dy),
      });
      return;
    }

    if (tool === 'pen') {
      currentPoints.current.push(pos);
      redraw();
      drawPen(ctx, currentPoints.current, color);
    } else if (tool === 'arrow' && startPoint.current) {
      redraw();
      drawArrow(
        ctx,
        startPoint.current.x,
        startPoint.current.y,
        pos.x,
        pos.y,
        color,
      );
    } else if (tool === 'eraser') {
      const hit = findNearestAnnotation(pos, 40);
      if (hit) onDeleteAnnotation(hit);
    }
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!drawMode || !isDrawing.current) return;
    const pos = getPos(e);
    isDrawing.current = false;

    if (tool === 'move' && movingAnnotation.current) {
      const m = movingAnnotation.current;
      const dx = pos.x - m.grabX;
      const dy = pos.y - m.grabY;
      const ann = savedAnnotations.find((a) => a.id === m.id);
      if (ann && (Math.abs(dx) > 2 || Math.abs(dy) > 2)) {
        const newData = translateAnnotation(
          { ...ann, data: m.origData },
          dx,
          dy,
        );
        onMoveAnnotation?.(m.id, newData);
      }
      movingAnnotation.current = null;
      setMovePreview(null);
      return;
    }

    if (tool === 'pen' && currentPoints.current.length > 1) {
      onAnnotationComplete('pen', { points: [...currentPoints.current] });
      currentPoints.current = [];
    } else if (tool === 'arrow' && startPoint.current) {
      const dx = pos.x - startPoint.current.x;
      const dy = pos.y - startPoint.current.y;
      if (Math.sqrt(dx * dx + dy * dy) > 10) {
        onAnnotationComplete('arrow', {
          x1: startPoint.current.x,
          y1: startPoint.current.y,
          x2: pos.x,
          y2: pos.y,
        });
      }
      startPoint.current = null;
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!drawMode || tool !== 'text') return;
    const pos = getPos(e);
    setTextInput({ x: pos.x, y: pos.y, value: '' });
    setTimeout(() => textInputRef.current?.focus(), 50);
  };

  const commitText = () => {
    if (!textInput || !textInput.value.trim()) {
      setTextInput(null);
      return;
    }
    onAnnotationComplete('text', {
      x: textInput.x,
      y: textInput.y,
      text: textInput.value.trim(),
      fontSize: 16,
    });
    setTextInput(null);
  };

  // Right-click to delete nearest annotation
  const handleContextMenu = (e: React.MouseEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    if (!drawMode) return;
    const hit = findNearestAnnotation(getPos(e), 30);
    if (hit) onDeleteAnnotation(hit);
  };

  // Canvas pixel dimensions match the image natural space (normalized 0-1000)
  return (
    <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 5 }}>
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        className="absolute inset-0 w-full h-full"
        style={{
          pointerEvents: drawMode ? 'all' : 'none',
          // Only valid CSS cursors: an invalid value is dropped by the
          // browser and the previous one (e.g. none) sticks.
          cursor: drawMode
            ? tool === 'text'
              ? 'text'
              : tool === 'eraser'
                ? 'cell'
                : tool === 'move'
                  ? 'move'
                  : 'crosshair'
            : undefined,
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
      />
      {/* Floating text input when placing text annotation */}
      {textInput && (
        <input
          ref={textInputRef}
          value={textInput.value}
          onChange={(e) =>
            setTextInput((t) => t && { ...t, value: e.target.value })
          }
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitText();
            if (e.key === 'Escape') setTextInput(null);
          }}
          onBlur={commitText}
          className="absolute bg-black/70 border border-white/30 text-white text-sm px-2 py-1 rounded outline-none focus:border-primary-600"
          style={{
            left: `${(textInput.x / width) * 100}%`,
            top: `${(textInput.y / height) * 100}%`,
            transform: 'translateY(-100%)',
            minWidth: 120,
            pointerEvents: 'all',
          }}
          placeholder={t('screens.annotationCanvas.typeLabel')}
        />
      )}
    </div>
  );
});
